/**
 * Demo terminals for ?mock=1: a Claude Code session you can take over, a shell someone else
 * is typing in, an ended session and an idle shell. Typing echoes locally once you have control.
 */
import type { TermControlRequest, TermOpenRequest, TermOpenResponse, TermSummary } from '@orgus/protocol';
import type { TerminalSource } from '@orgus/ui';
import { ApiError } from '../../lib/api.ts';
import { MOCK_IDS } from '../../lib/mock.ts';

const MIN = 60_000;
const E = '\u001b[';
const dim = (s: string) => `${E}2m${s}${E}0m`;
const bold = (s: string) => `${E}1m${s}${E}0m`;
const truecolor = (r: number, g: number, b: number, s: string) => `${E}38;2;${r};${g};${b}m${s}${E}0m`;
const green = (s: string) => `${E}32m${s}${E}0m`;
const red = (s: string) => `${E}31m${s}${E}0m`;
const blue = (s: string) => `${E}34m${s}${E}0m`;
const NL = '\r\n';

export const MOCK_YOU = 'owner';

function claudeScreen(): string {
  const orange = (s: string) => truecolor(215, 119, 87, s);
  return [
    `${orange('✻')} Welcome to ${bold('Claude Code')}`,
    dim('  cwd: C:/Users/Srika/CascadeProjects/dev-portal'),
    '',
    `${dim('>')} Sessions page: grouped list, filters and the detail drawer`,
    '',
    `${green('●')} Read(apps/web/src/features/sessions/SessionsView.tsx)`,
    dim('  ⎿  Read 212 lines'),
    `${green('●')} Update(apps/web/src/features/sessions/SessionRow.tsx)`,
    dim('  ⎿  Updated with 14 additions and 3 removals'),
    `${green('●')} Bash(pnpm --filter @orgus/web test)`,
    `  ⎿  Test Files  ${green('5 passed')} (5)`,
    `     Tests  ${green('55 passed')} (55)`,
    '',
    `${green('●')} Grouping by project then machine works. The drawer timeline renders`,
    '  subagents as nested lanes. Next: keyboard navigation between rows.',
    '',
    `${orange('✻')} Wiring j and k in the drawer${dim('… (esc to interrupt)')}`,
    '',
    `${dim('╭──────────────────────────────────────────────────────────────╮')}`,
    `${dim('│')} >                                                            ${dim('│')}`,
    `${dim('╰──────────────────────────────────────────────────────────────╯')}`,
    dim('  ⏵⏵ accept edits on'),
  ].join(NL);
}

const PROMPT = `${green('srikanth@mbp16')} ${blue('billing-service')} % `;

function shellScreen(): string {
  return [
    `${PROMPT}git status`,
    'On branch stripe-retry',
    "Your branch is ahead of 'origin/main' by 2 commits.",
    '',
    'Changes not staged for commit:',
    `\t${red('modified:   src/webhooks/retry.ts')}`,
    '',
    `${PROMPT}pnpm test --filter webhooks`,
    ` ${green('✓')} retries a failed webhook with the event id as the key (41 ms)`,
    ` ${green('✓')} gives up after five attempts (12 ms)`,
    ` ${red('✗')} backfills last week ${dim('(pending)')}`,
    '',
    `${PROMPT}`,
  ].join(NL);
}

const PS = 'PS C:\\Users\\Srika\\CascadeProjects\\dev-portal> ';

function base(now: number): TermSummary[] {
  return [
    {
      id: 'trm_demo_session',
      machineId: MOCK_IDS.studio,
      kind: 'session',
      backend: 'pty',
      cwd: 'C:/Users/Srika/CascadeProjects/dev-portal',
      title: 'Claude Code: Sessions page: grouped list, filters and the detail drawer',
      sessionId: 'f0c9e1a2-sessions-page',
      model: 'claude-opus-5',
      pid: 18244,
      cols: 100,
      rows: 26,
      state: 'live',
      startedAt: now - 26 * MIN,
      endedAt: null,
      exitCode: null,
      openedBy: MOCK_YOU,
      lock: null,
      viewers: 2,
      recording: 'term/trm_demo_session.cast',
    },
    {
      id: 'trm_demo_billing',
      machineId: MOCK_IDS.mbp,
      kind: 'shell',
      backend: 'pty',
      cwd: '/Users/srikanth/code/billing-service',
      title: 'Shell: billing-service',
      sessionId: null,
      model: null,
      pid: 5120,
      cols: 96,
      rows: 24,
      state: 'live',
      startedAt: now - 12 * MIN,
      endedAt: null,
      exitCode: null,
      openedBy: 'priya',
      lock: { actor: 'priya', label: 'priya', since: now - 3 * MIN, lastInputAt: now - 20_000 },
      viewers: 2,
      recording: 'term/trm_demo_billing.cast',
    },
    {
      id: 'trm_demo_portal',
      machineId: MOCK_IDS.studio,
      kind: 'shell',
      backend: 'pty',
      cwd: 'C:/Users/Srika/CascadeProjects/dev-portal',
      title: 'Shell: dev-portal',
      sessionId: null,
      model: null,
      pid: 20480,
      cols: 100,
      rows: 26,
      state: 'live',
      startedAt: now - 4 * MIN,
      endedAt: null,
      exitCode: null,
      openedBy: MOCK_YOU,
      lock: null,
      viewers: 1,
      recording: 'term/trm_demo_portal.cast',
    },
    {
      id: 'trm_demo_nightly',
      machineId: MOCK_IDS.build,
      kind: 'session',
      backend: 'pty',
      cwd: '/srv/checkouts/billing-service',
      title: 'Claude Code: Nightly dependency upgrade',
      sessionId: '1f6e2a9c-nightly-deps',
      model: 'claude-haiku-5-5',
      pid: null,
      cols: 100,
      rows: 26,
      state: 'exited',
      startedAt: now - 4 * 60 * MIN,
      endedAt: now - 3 * 60 * MIN - 13 * MIN,
      exitCode: 0,
      openedBy: MOCK_YOU,
      lock: null,
      viewers: 0,
      recording: 'term/trm_demo_nightly.cast',
    },
  ];
}

const SCREENS: Record<string, () => string> = {
  trm_demo_session: claudeScreen,
  trm_demo_billing: shellScreen,
  trm_demo_portal: () => `Orgus demo shell${NL}${PS}`,
  trm_demo_nightly: () => `${green('●')} Bumped stripe to 18.2. Tests pass.${NL}${NL}${dim('Session ended.')}${NL}`,
};

class MockTerms {
  private terms: TermSummary[] | null = null;
  private readonly listeners = new Set<() => void>();
  private n = 0;

  private all(): TermSummary[] {
    this.terms ??= base(Date.now());
    return this.terms;
  }

  private changed(): void {
    for (const l of this.listeners) l();
  }

  subscribe(l: () => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  list(): TermSummary[] {
    return [...this.all()];
  }

  get(id: string): TermSummary | undefined {
    return this.all().find((t) => t.id === id);
  }

  private update(id: string, patch: Partial<TermSummary>): TermSummary {
    const t = this.get(id);
    if (!t) throw new ApiError({ what: 'That terminal is not open.', why: 'It may have ended.', next: 'Open a new terminal.' }, 404);
    const next = { ...t, ...patch };
    this.terms = this.all().map((x) => (x.id === id ? next : x));
    this.changed();
    return next;
  }

  open(req: TermOpenRequest): TermOpenResponse {
    if (req.mode === 'attach') {
      const hit = this.all().find((t) => t.sessionId === req.sessionId && t.state !== 'exited');
      if (hit) return { term: hit, existing: true };
      throw new ApiError({ what: 'Orgus only watches this session.', why: 'It was not started in a terminal Orgus owns.', next: 'Start a new session in a terminal.' }, 403);
    }
    const id = `trm_demo_new${++this.n}`;
    const signIn = req.mode === 'sign-in';
    const cwd = signIn ? `C:/Users/you/.orgus/claude-accounts/${req.launchAccountId ?? 'account'}` : (req.cwd ?? 'C:/Users/Srika/CascadeProjects/dev-portal');
    const name = cwd.split(/[\\/]/).filter(Boolean).pop() ?? cwd;
    const t: TermSummary = {
      id,
      machineId: req.machineId,
      kind: req.mode === 'shell' ? 'shell' : signIn ? 'sign-in' : 'session',
      backend: 'pty',
      cwd,
      title: req.mode === 'shell' ? `Shell: ${name}` : signIn ? 'Sign in: demo account' : `Claude Code: ${req.prompt?.trim() || 'new session'}`,
      sessionId: req.mode === 'shell' || signIn ? null : `demo-${this.n}`,
      model: req.model ?? null,
      pid: 30000 + this.n,
      cols: req.cols ?? 100,
      rows: req.rows ?? 26,
      state: 'live',
      startedAt: Date.now(),
      endedAt: null,
      exitCode: null,
      openedBy: MOCK_YOU,
      lock: null,
      viewers: 1,
      recording: `term/${id}.cast`,
    };
    SCREENS[id] = req.mode === 'shell'
      ? () => `Orgus demo shell${NL}${PS}`
      : signIn
        ? () => `Opening browser to sign in…${NL}If the browser didn't open, visit: ${dim('https://claude.com/cai/oauth/authorize?code=true&client_id=demo')}${NL}Paste code here if prompted > `
        : () => `${truecolor(215, 119, 87, '✻')} Welcome to ${bold('Claude Code')}${NL}${NL}${dim('>')} ${req.prompt ?? ''}${NL}`;
    this.terms = [t, ...this.all()];
    this.changed();
    return { term: t, existing: false };
  }

  control(id: string, body: TermControlRequest): TermSummary {
    const t = this.get(id);
    if (!t) return this.update(id, {});
    const now = Date.now();
    if (body.action === 'release') return this.update(id, { lock: null });
    if (t.lock && t.lock.actor !== MOCK_YOU && body.action !== 'force') {
      throw new ApiError({ what: `${t.lock.label} is typing.`, why: 'Exactly one person can type into a terminal at a time.', next: 'Ask them to hand it back, or an admin can take control.' }, 409);
    }
    return this.update(id, { lock: { actor: MOCK_YOU, label: MOCK_YOU, since: now, lastInputAt: now } });
  }

  close(id: string): TermSummary {
    return this.update(id, { state: 'exited', endedAt: Date.now(), exitCode: 0, lock: null });
  }

  /** The demo screen, echoing what you type while you have control. */
  source(id: string): TerminalSource {
    const listeners = new Set<(d: string, reset: boolean) => void>();
    const kind = this.get(id)?.kind;
    return {
      subscribe: (l) => {
        listeners.add(l);
        const screen = SCREENS[id]?.() ?? '';
        setTimeout(() => l(screen, true), 250);
        return () => listeners.delete(l);
      },
      input: (data) => {
        const out = data.replace(/\u001b/g, '').replace(/\r/g, kind === 'shell' ? `${NL}${PS}` : `${NL}${dim('(demo) Claude would answer here.')}${NL}> `).replace(/\u007f/g, '\b \b');
        for (const l of listeners) l(out, false);
      },
      resize: () => undefined,
    };
  }
}

export const mockTerms = new MockTerms();
